import log from "loglevel";

/**
 * Provides means to switch between states
 * @param {String} name The state machine name (for logging purpose).
 * @param {Object} shared_data Any data to be shared with the states' in/out functions.
 * @param {Object} states The states description with the following items:
 *      {
 *          state_i: {
 *              in: (shared_data, statemachine) => { statements to be executed when entering this state },
 *              out: (shared_data, statemachine) => { statements to be executed when leaving this state },
 *              transit: { describe the transitions from this state. to be completed... },
 *              schedule: { to be completed... }
 *          },
 *          state_j: {...}
 *      }
 * @param {String} default_state_name
 */
const StateMachine = function ({name, shared_data = {}, states = {}, default_state_name}) {
    let state = 0,
        states_index = [],
        default_state = 0;
    let schedules = [];

    const logger = log.getLogger(`statemachine.${name}`);
    // logger.setLevel(0);

    this.states = {};
    this.transitions = {};
    this.schedules = {};

    Object.defineProperty(this, "name", {
        get: () => name,
        set: () => {}
    });

    Object.defineProperty(this, "state", {
        get: () => states_index[state],
        set: () => {}
    });

    Object.defineProperty(this, "state_names", {
        get: () => Object.keys(this.states),
        set: () => {}
    });

    this.data = shared_data;

    this.set_states = function (_states) {
        for (let _state_name in _states) {
            let _state_descriptor = _states[_state_name];
            let _state = {
                symbol: states_index.length,
                name: _state_name,
                handler: {
                    in: _state_descriptor.in,
                    out: _state_descriptor.out
                },
                data: {},
                transit: [],
                schedule: _state_descriptor.schedule
            };
            let _state_index = states_index.push(_state) - 1;
            this.states[_state_name] = _state;
            logger.info(`sm ${name}: new state ${_state_name}`);
            schedules.push([]);
            this.schedules[_state_name] = {};
            for (let _schedule_name in _state.schedule) {
                let _schedule_index = schedules[_state_index].push(_state.schedule[_schedule_name]) - 1;
                schedules[_state_index][_schedule_index].task_id = null;
                this.schedules[_state_name][_schedule_name] = schedules[_state_index][_schedule_index];
            }
        }
        for (let _state_name in _states) {
            let _state = _states[_state_name];
            this.transitions[_state_name] = {};
            for (let _state_index = 0; _state_index < states.length; _state_index++) {
                this.states[_state_name].transit[_state_index] = false;
            }
            for (let _transit_name in _state.transit) {
                let _transit = _states[_state_name].transit[_transit_name];
                let _transit_state = _transit.state;
                let _transit_symbol = this.states[_transit_state].symbol;
                this.states[_state_name].transit[_transit_symbol] = {
                    symbol: _transit_symbol,
                    name: _transit_name,
                    handler: _transit.handler
                };
                this.transitions[_state_name][_transit_name] = this.states[_state_name].transit[_transit_symbol];
                logger.info(`sm ${name}:  new transition state ${_state_name} -> ${_transit_state} (${_transit_name})`);
            }
        }
    };

    this.set_states(states);

    this.set_default_state = function (_default_state_name) {
        default_state = this.states[_default_state_name].symbol;
        state = default_state;
        states_index[state].handler.in(states_index[state].data, this);
    };

    if (default_state_name !== undefined) {
        this.set_default_state(default_state_name);
    }

    this.get_current_state_data = function () {
        return states_index[state].data;
    };

    this.is_in_state = function (_state) {
        return state === _state;
    };

    this.is_in_state_by_name = function (_state_name) {
        let _state = this.states[_state_name];
        return this.is_in_state(_state);
    };

    let transition = (_transition, _transition_data) => {
        let _state_data_1 = states_index[state].data;
        let _state_data_2 = states_index[_transition].data;
        let _state_name_1 = states_index[state].name;
        let _state_name_2 = states_index[_transition].name;
        let _transition_name;
        let _data_key;
        if (!states_index[state].transit[_transition]) {
            logger.info(`sm ${name}: transition ${_state_name_1} -> ${_state_name_2} is not defined`);
            return Promise.resolve(false);
        }
        _transition_name = states_index[state].transit[_transition].name;
        logger.info(`sm ${name}: check autorization to transit ${_state_name_1} -> ${_state_name_2} (${_transition_name})`);

        logger.info(states_index[state].transit[_transition]);

        let _authorisation = states_index[state].transit[_transition].handler(this, _transition_data);
        if (typeof _authorisation === "boolean") {
            _authorisation = Promise.resolve(_authorisation);
        } else if (typeof _authorisation !== "object" && _authorisation.constructor.name !== "Promise") {
            return Promise.resolve(false);
        }

        return _authorisation.then((_authorisation) => {
            if (!_authorisation) {
                logger.info(`sm ${name}: transit ${_state_name_1} -> ${_state_name_2} (${_transition_name}) prohibited, sorry`);
                return false;
            }
            logger.info(`sm ${name}: transit ${_state_name_1} -> ${_state_name_2} (${_transition_name}) authorized`);
            for (_data_key in _transition_data) {
                _state_data_2[_data_key] = _transition_data[_data_key];
            }
            logger.info(`sm ${name}: checking out from state ${_state_name_1}`);
            states_index[state].handler.out(_state_data_1, this);

            for (let _schedule_index = 0; _schedule_index < schedules[state].length; _schedule_index++) {
                window.clearInterval(schedules[state][_schedule_index].task_id);
                schedules[state][_schedule_index].task_id = null;
            }
            state = _transition;
            logger.info(`sm ${name}: checking in to state ${_state_name_2}`, this);
            states_index[state].handler.in(_state_data_2, this);
            schedules[state].forEach((_schedule) => {
                _schedule.task_id = window.setInterval(() => {
                    if ((_schedule.repeat -= 1) <= 0) {
                        window.clearInterval(_schedule.task_id);
                    }
                    _schedule.handler(_state_data_2);
                }, _schedule.interval);
            });
            logger.info(`sm ${name}: transit ${_state_name_1} -> ${_state_name_2} (${_transition_name}) succesful`);
            return true;
        });
    };

    this.transition = {};
    states_index.forEach((_state) => {
        this.transition[_state.name] = (_transition_data) => transition(_state.symbol, _transition_data);
    });

    this.transition_by_name = function (_transition_name, _transition_data) {
        let _state_name = states_index[state].name;
        let _transition_symbol;
        if (!(_transition_name in this.transitions[_state_name])) {
            logger.info(`sm ${name}: transition ${_state_name} -> ? (${_transition_name}) is not defined`);
            return Promise.resolve(false);
        }
        _transition_symbol = this.transitions[_state_name][_transition_name].symbol;
        return this.transition(_transition_symbol, _transition_data);
    };

    logger.info(`sm ${name}: checking in to state ${states_index[state].name}`);
    states_index[state].handler.in(states_index[state].data, this);

    schedules[state].forEach((_schedule) => {
        _schedule.task_id = window.setInterval(() => {
            if ((_schedule.repeat -= 1) <= 0) {
                window.clearInterval(_schedule.task_id);
            }
            _schedule.handler(states_index[state].data);
        }, _schedule.interval);
    });

    states = undefined;
};

export default StateMachine;
